import { action, observable, makeObservable, computed } from 'mobx';
import { controlCenterApi } from '$src/api/control-center-api';
import { HTTP_STATUS_CODE_NOT_FOUND } from '$src/api/constants';
import { sendCloudwatchRUMError } from '$src/utils/cloudwatchRUM';
import pick from 'lodash/pick';
import omit from 'lodash/omit';

import { Notify } from '$src/utils/notify';

const contactPreferenceNames = ['serviceWorkerSms', 'unknownPersonSms', 'vsgModeStatusSms', 'deliverySms'];
const omittedPreferencesFields = ['onboardingComplete', 'onboardingCompleteTime'];

export default class PreferencesStore {
  toggleSide = true;
  preferences = {};
  contacts = [];
  updatingPreferences = false;

  fetchPreferences = async (userId, locationId, getContacts = true) => {
    try {
      const [preferences, contacts] = await Promise.all([
        controlCenterApi.fetchPreferences(userId, locationId),
        getContacts ? controlCenterApi.fetchContacts(userId, locationId) : [],
      ]);
      this.setPreferences(preferences);
      this.setContacts(contacts);
    } catch (e) {
      if (e.response.status === HTTP_STATUS_CODE_NOT_FOUND) {
        sendCloudwatchRUMError('Alert preferences not found', e);
      } else {
        Notify.error('There was an error getting the user preferences', e);
      }
    }
  };

  fetchContacts = async (userId, locationId) => {
    try {
      const contacts = await controlCenterApi.fetchContacts(userId, locationId);
      this.setContacts(contacts);
    } catch (e) {
      Notify.error('There was an error getting contacts', e);
    }
  };

  putPreferences = async (preferencesUpdated, userId, locationId) => {
    this.setUpdatingPreferences(true);
    const curatedPreferences = omit(preferencesUpdated, omittedPreferencesFields);
    await controlCenterApi.updatePreferences(curatedPreferences, userId, locationId);
    this.setPreferences(preferencesUpdated);
    this.setUpdatingPreferences(false);
  };

  setPreferences = (preferences) => {
    this.preferences = preferences;
  };

  setContacts = (contacts) => {
    this.contacts = contacts;
  };

  setUpdatingPreferences = (value) => {
    this.updatingPreferences = value;
  };

  setToggleSide = (value) => {
    this.toggleSide = value;
  };

  get alertPreferences() {
    return pick(this.preferences, contactPreferenceNames);
  }

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      updatingPreferences: observable,
      toggleSide: observable,
      preferences: observable,
      contacts: observable,
      fetchPreferences: action,
      fetchContacts: action,
      putPreferences: action,
      setPreferences: action,
      setUpdatingPreferences: action,
      setContacts: action,
      setToggleSide: action,
      alertPreferences: computed,
    });
  }
}
